'use client';

// module imports
import { useState } from 'react';

// file imports
import { QuestionSortableList } from '@/components';
import { replacePlaceholders } from '@/constants';
import { ContactFeedback, IQuestion, QuestionOption, QuestionOptionObject, SocialFeedback } from '@/types';
interface ButtonProps {
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
  variant?: 'primary' | 'secondary' | 'outline';
  className?: string;
}

const Button = ({ onClick, disabled = false, children, variant = 'primary', className = '' }: ButtonProps) => {
  const baseClasses = 'w-full h-10 font-semibold rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer shadow-md hover:shadow-lg text-sm';

  const variantClasses = {
    primary: 'bg-[#5167b2] text-white hover:bg-[#4158a2]',
    secondary: 'bg-gray-100 text-gray-700 hover:bg-gray-200',
    outline: 'border-2 border-[#5167b2] text-[#5167b2] hover:bg-[#5167b2] hover:text-white',
  };

  return (
    <button onClick={onClick} disabled={disabled} className={`${baseClasses} ${variantClasses[variant]} ${className}`}>
      {children}
    </button>
  );
};

interface InputProps {
  type?: string;
  placeholder?: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  error?: boolean;
  className?: string;
}

const Input = ({ type = 'text', placeholder = '', value, onChange, disabled = false, error = false, className = '' }: InputProps) => {
  const baseClasses = 'w-full h-10 border-2 rounded-xl px-2 py-1 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 transition-all duration-200 text-sm';
  const errorClasses = error ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' : 'border-gray-200 focus:border-[#5167b2] focus:ring-[#5167b2]/20';
  const disabledClasses = disabled ? 'bg-gray-100 cursor-not-allowed' : '';

  return <input type={type} className={`${baseClasses} ${errorClasses} ${disabledClasses} ${className}`} placeholder={placeholder} value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled} />;
};

interface OptionButtonProps {
  option: string | { label: string; required: boolean };
  isSelected: boolean;
  onClick: () => void;
  disabled?: boolean;
  showRequired?: boolean;
}

const OptionButton = ({ option, isSelected, onClick, disabled = false, showRequired = false }: OptionButtonProps) => {
  const optionValue = typeof option === 'string' ? option : option.label;
  const isRequired = typeof option === 'object' ? option.required : false;

  return (
    <button onClick={onClick} disabled={disabled} className={`w-full h-12 bg-white border-2 rounded-xl px-2 py-1 flex items-center justify-center transition-all duration-200 disabled:opacity-50 cursor-pointer shadow-sm hover:shadow-md ${isSelected ? 'border-[#5167b2] bg-[#5167b2]/5' : 'border-gray-200 hover:border-[#5167b2]/50 hover:bg-gray-50'}`}>
      <span className="text-xs font-semibold text-gray-800">
        {optionValue}
        {showRequired && isRequired && <span className="text-red-500 ml-1">*</span>}
      </span>
    </button>
  );
};

interface LabelProps {
  children: React.ReactNode;
  required?: boolean;
  className?: string;
}

const Label = ({ children, required = false, className = '' }: LabelProps) => (
  <label className={`block text-xs font-semibold text-gray-800 ${className}`}>
    {children}
    {required && <span className="text-red-500 ml-1">*</span>}
  </label>
);

// Email validation helper function
const isValidEmail = (email: string) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email.trim());
};

// Render question input exactly like FeedbackPage
const renderQuestionInput = (question: IQuestion) => {
  // Handle contact type questions first (before generic options)
  if (question.type === 'contact') {
    // Handle contact data directly
    const contactData: ContactFeedback = {};

    // Check if under-18 option is selected
    const hasUnder18Option = question.options?.some((opt: QuestionOption) => typeof opt === 'object' && opt.isUnder18Option && contactData['i am speaking to an audience under 18'] === 'true');

    // Check if under-18 option is required
    const isUnder18Required = question.options?.some((opt: QuestionOption) => typeof opt === 'object' && opt.isUnder18Option && opt.required);

    return (
      <div className="space-y-3">
        {/* Direct Contact Input Fields */}
        {question.options && question.options.length > 0 && (
          <div className="space-y-3">
            {question.options.map((option: QuestionOption, index: number) => {
              const optionLabel = typeof option === 'string' ? option : option.label;
              const isRequired = typeof option === 'object' ? option.required : false;
              const isUnder18Option = typeof option === 'object' && option.isUnder18Option;
              const fieldKey = optionLabel.toLowerCase().replace('ask for attendee ', '').replace('ask for ', '');
              const fieldValue = contactData[fieldKey] || '';

              // Handle the under-18 option as a checkbox (only show if both required and isUnder18Option are true)
              if (isUnder18Option && isRequired) {
                return (
                  <div key={index} className="space-y-3">
                    <div className="flex items-start space-x-3">
                      <input type="checkbox" id={`under18-${question._id}`} checked={false} disabled className="mt-1 w-3 h-3 text-[#5167b2] bg-gray-100 border-gray-300 rounded focus:ring-[#5167b2] focus:ring-2" />
                      <label htmlFor={`under18-${question._id}`} className="text-xs text-gray-700 cursor-pointer">
                        I am over 18 years of age
                      </label>
                    </div>
                    {isUnder18Required && <p className="text-xs text-red-600 bg-red-50 p-2 rounded border border-red-200 ml-7">Please confirm you are over 18 years of age to enable personal information fields.</p>}
                  </div>
                );
              }

              // Skip rendering under-18 option if it's not required
              if (isUnder18Option && !isRequired) {
                return null;
              }

              // Use inputLabel, inputType, and placeholder from question option if available
              const optionInputLabel = typeof option === 'object' ? option.inputLabel : '';
              const optionInputType = typeof option === 'object' ? option.inputType : '';
              const optionPlaceholder = typeof option === 'object' ? option.placeholder : '';

              const inputType = optionInputType || 'text';
              let placeholder = optionPlaceholder || 'Enter your input...';
              const label = optionInputLabel || fieldKey.charAt(0).toUpperCase() + fieldKey.slice(1);
              let isDisabled = false;

              // Handle special cases for under-18 option
              if (isUnder18Required && !hasUnder18Option) {
                if (fieldKey.includes('email')) {
                  placeholder = 'Email disabled - please confirm you are over 18';
                  isDisabled = true;
                } else if (fieldKey.includes('name')) {
                  placeholder = 'Name disabled - please confirm you are over 18';
                  isDisabled = true;
                }
              }

              // Check if email field has validation error
              const isEmailField = fieldKey.includes('email');
              const hasEmailError = isEmailField && fieldValue && !isValidEmail(fieldValue);

              return (
                <div key={index} className="space-y-3">
                  <Label required={isRequired}>{label}</Label>
                  <Input type={inputType} placeholder={placeholder} value={fieldValue} onChange={() => {}} disabled={isDisabled} error={!!hasEmailError} />
                  {hasEmailError && <p className="text-red-500 text-sm font-medium">Please enter a valid email address</p>}
                </div>
              );
            })}
          </div>
        )}

        {/* Continue Button */}
        <button disabled className="w-full h-10 bg-[#5167b2] text-white font-semibold rounded-xl hover:bg-[#4158a2] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer shadow-md hover:shadow-lg text-sm">
          Continue
        </button>
      </div>
    );
  }

  // Handle social type questions
  if (question.type === 'social') {
    const socialData: SocialFeedback = {};

    return (
      <div className="space-y-3">
        {/* Social Media Input Fields */}
        {question.options && question.options.length > 0 && (
          <div className="space-y-3">
            {question.options.map((platform: QuestionOption, index: number) => {
              const platformName = typeof platform === 'string' ? platform : platform.label;
              const isRequired = typeof platform === 'object' ? platform.required : false;
              const platformKey = platformName.toLowerCase();
              const fieldValue = socialData[platformKey] || '';

              // Use inputLabel, inputType, and placeholder from question option if available
              const optionInputLabel = typeof platform === 'object' ? platform.inputLabel : '';
              const optionInputType = typeof platform === 'object' ? platform.inputType : '';
              const optionPlaceholder = typeof platform === 'object' ? platform.placeholder : '';

              const placeholder = optionPlaceholder || `Enter your ${platformName} URL...`;
              const label = optionInputLabel || platformName;
              const inputType = optionInputType || 'url';

              return (
                <div key={index} className="space-y-3">
                  <Label required={isRequired}>{label}</Label>
                  <Input type={inputType} placeholder={placeholder} value={fieldValue} onChange={() => {}} />
                </div>
              );
            })}
          </div>
        )}

        {/* Continue Button */}
        <button disabled className="w-full h-10 bg-[#5167b2] text-white font-semibold rounded-xl hover:bg-[#4158a2] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer shadow-md hover:shadow-lg text-sm">
          Continue
        </button>
      </div>
    );
  }

  if (question.options && question.options.length > 0) {
    return (
      <div className="space-y-2">
        {question.options.map((option, index) => {
          return <OptionButton key={index} option={option} isSelected={false} onClick={() => {}} disabled={true} showRequired={true} />;
        })}
      </div>
    );
  }

  switch (question.category) {
    case 'boolean':
      return (
        <div className="space-y-2">
          <OptionButton option="Yes" isSelected={false} onClick={() => {}} disabled={true} />
          <OptionButton option="No" isSelected={false} onClick={() => {}} disabled={true} />
        </div>
      );

    case 'rating':
      return (
        <div className="space-y-3">
          <div className="flex justify-center space-x-2">
            {[1, 2, 3, 4, 5].map((rating) => (
              <button key={rating} disabled className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold transition-all duration-200 cursor-not-allowed shadow-sm bg-gray-100 text-gray-600">
                {rating}
              </button>
            ))}
          </div>
          <div className="text-center text-xs text-gray-500">Select a rating</div>
        </div>
      );

    case 'star':
      return (
        <div className="space-y-3">
          <div className="flex justify-center space-x-1">
            {[1, 2, 3, 4, 5].map((rating) => (
              <button key={rating} disabled className="w-8 h-8 flex items-center justify-center transition-all duration-200 cursor-not-allowed text-gray-300">
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
              </button>
            ))}
          </div>
          <div className="text-center text-xs text-gray-500">Select a rating</div>
        </div>
      );

    case 'scale':
      return (
        <div className="space-y-2">
          {question.options && question.options.length > 0 ? (
            <div className="space-y-2">
              {question.options.slice(0, 3).map((option, index) => {
                const optionValue = typeof option === 'string' ? option : option.label;
                return (
                  <div key={index} className="w-full h-12 bg-gray-100 border border-gray-200 rounded-lg px-3 py-2 flex items-center justify-center cursor-not-allowed">
                    <span className="text-sm font-medium text-gray-600 truncate">{optionValue}</span>
                  </div>
                );
              })}
              {question.options.length > 3 && <div className="text-center text-xs text-gray-500">+{question.options.length - 3} more options</div>}
            </div>
          ) : (
            <div className="flex justify-center space-x-2">
              {[1, 2, 3, 4, 5].map((rating) => (
                <button key={rating} disabled className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold transition-all duration-200 cursor-not-allowed shadow-sm bg-gray-100 text-gray-600">
                  {rating}
                </button>
              ))}
            </div>
          )}
          <div className="text-center text-xs text-gray-500">Scale rating</div>
        </div>
      );

    case 'nps':
      return (
        <div className="space-y-3">
          <div className="flex justify-center space-x-1">
            {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((score) => (
              <button key={score} disabled className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold transition-all duration-200 cursor-not-allowed shadow-sm bg-gray-100 text-gray-600">
                {score}
              </button>
            ))}
          </div>
          <div className="flex justify-between text-xs text-gray-400">
            <span>Not likely</span>
            <span>Very likely</span>
          </div>
          <div className="text-center text-xs text-gray-500">NPS Score (0-10)</div>
        </div>
      );

    case 'emoji':
      const emojiOptions = question.options && question.options.length > 0 ? question.options : ['😡', '😞', '😐', '🙂', '😀'];

      return (
        <div className="space-y-3">
          <div className="flex justify-center space-x-2">
            {emojiOptions.map((emoji, index) => {
              // Handle both string and object options for emojis
              const emojiValue = typeof emoji === 'string' ? emoji : (emoji as QuestionOptionObject).label;

              return (
                <button key={index} disabled className="w-12 h-12 rounded-full flex items-center justify-center text-xl transition-all duration-200 cursor-not-allowed shadow-sm hover:bg-gray-50">
                  {emojiValue}
                </button>
              );
            })}
          </div>
        </div>
      );

    case 'text':
    default:
      if (question.type === 'booking') {
        return (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <input type="date" className="w-full h-10 border-2 border-gray-200 rounded-xl px-2 py-1 text-gray-900 focus:border-[#5167b2] focus:outline-none focus:ring-2 focus:ring-[#5167b2]/20 transition-all duration-200 text-sm" value="" disabled />
              <input type="time" className="w-full h-10 border-2 border-gray-200 rounded-xl px-2 py-1 text-gray-900 focus:border-[#5167b2] focus:outline-none focus:ring-2 focus:ring-[#5167b2]/20 transition-all duration-200 text-sm" value="" disabled />
            </div>
            <button disabled className="w-full h-10 bg-[#5167b2] text-white font-semibold rounded-xl hover:bg-[#4158a2] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer shadow-md hover:shadow-lg text-sm">
              Continue
            </button>
          </div>
        );
      }

      // Default text input
      const isDescribeTalkQuestion = question.label === 'Describe this talk';

      // Get placeholder from question options if available
      const getTextPlaceholder = () => {
        if (isDescribeTalkQuestion) {
          return 'Your detailed insight is invaluable!';
        }

        // Check if question has options with placeholders
        if (question.options && question.options.length > 0) {
          const firstOption = question.options[0];
          if (typeof firstOption === 'object' && firstOption.placeholder) {
            return firstOption.placeholder;
          }
        }

        return 'Type your answer here...';
      };

      return (
        <div className="space-y-3">
          <textarea className="w-full min-h-[80px] border-2 border-gray-200 rounded-xl px-2 py-1 text-gray-900 placeholder-gray-500 focus:border-[#5167b2] focus:outline-none focus:ring-2 focus:ring-[#5167b2]/20 transition-all duration-200 resize-none text-sm" rows={3} placeholder={getTextPlaceholder()} value="" disabled />

          {/* Consent Checkboxes - Only for "Describe this talk" question */}
          {isDescribeTalkQuestion && (
            <div className="space-y-2">
              <div className="flex items-start space-x-2">
                <input type="checkbox" id={`consent-share-${question._id}`} checked={false} disabled className="mt-1 w-3 h-3 text-[#5167b2] bg-gray-100 border-gray-300 rounded focus:ring-[#5167b2] focus:ring-2" />
                <label htmlFor={`consent-share-${question._id}`} className="text-xs text-gray-700 cursor-pointer">
                  My feedback can be shown publicly.
                </label>
              </div>

              <div className="flex items-start space-x-2">
                <input type="checkbox" id={`consent-name-${question._id}`} checked={false} disabled className="mt-1 w-3 h-3 text-[#5167b2] bg-gray-100 border-gray-300 rounded focus:ring-[#5167b2] focus:ring-2" />
                <label htmlFor={`consent-name-${question._id}`} className="text-xs text-gray-700 cursor-pointer">
                  My name and organization can also be included in public feedback.
                </label>
              </div>
            </div>
          )}

          <Button onClick={() => {}} disabled={true}>
            Continue
          </Button>
        </div>
      );
  }
};

interface QuestionSidebarProps {
  questions: IQuestion[];
  selectedQuestion: IQuestion | null;
  onQuestionSelect: (question: IQuestion) => void;
  onToggleQuestion?: (questionId: string) => void;
  onReorder?: (newOrder: IQuestion[]) => void;
  canEditQuestions?: boolean;
  speakerName?: string;
  activeTab?: 'questions' | 'preview';
  onTabChange?: (tab: 'questions' | 'preview') => void;
}

export default function QuestionSidebar({ questions, selectedQuestion, onQuestionSelect, onToggleQuestion, onReorder, canEditQuestions = false, speakerName = 'Speaker', activeTab = 'questions', onTabChange }: QuestionSidebarProps) {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [showTooltip, setShowTooltip] = useState(false);

  const handleMouseMove = (e: React.MouseEvent) => {
    setMousePosition({ x: e.clientX, y: e.clientY });
  };

  const handleMouseEnter = () => {
    setShowTooltip(true);
  };

  const handleMouseLeave = () => {
    setShowTooltip(false);
  };
  return (
    <div className="w-80 bg-white border border-gray-200 rounded-lg">
      <div className="p-4 border-b border-gray-200">
        <div className="flex gap-2">
          <button onClick={() => onTabChange?.('questions')} className={`px-3 py-1 rounded text-sm font-medium cursor-pointer transition-colors ${activeTab === 'questions' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}>
            All Questions
          </button>
          <button onClick={() => onTabChange?.('preview')} className={`px-3 py-1 rounded text-sm font-medium cursor-pointer transition-colors ${activeTab === 'preview' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}>
            Preview Question
          </button>
        </div>
      </div>
      <div className="p-4 space-y-2 max-h-96 overflow-y-auto">
        {activeTab === 'questions' ? (
          questions.length > 0 ? (
            <QuestionSortableList
              questions={questions}
              selectedQuestion={selectedQuestion}
              canEditQuestions={canEditQuestions}
              onReorder={onReorder || (() => {})}
              onQuestionSelect={(question) => {
                const questionWithSpeakerName = {
                  ...question,
                  description: question.description ? replacePlaceholders(question.description, speakerName) : undefined,
                };
                onQuestionSelect(questionWithSpeakerName);
              }}
              onToggleQuestion={onToggleQuestion || (() => {})}
            />
          ) : (
            <div className="text-center text-gray-500 py-4">No questions available</div>
          )
        ) : (
          <div className="space-y-4">
            {selectedQuestion ? (
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm relative group hover:opacity-75 transition-all duration-300" onMouseMove={handleMouseMove} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
                {/* Header with profile picture and progress */}
                <div className="p-3 text-center border-b border-gray-100 pointer-events-none">
                  <div className="w-10 h-10 mx-auto mb-2 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center overflow-hidden">
                    <span className="text-sm font-bold text-white">{speakerName.charAt(0)}</span>
                  </div>
                  <p className="text-xs text-gray-600 mb-1">Feedback 0% complete for</p>
                  <span className="font-semibold text-xs">{speakerName}</span>
                  <div className="w-full bg-gray-200 rounded-full h-1 mt-2">
                    <div className="bg-blue-600 h-1 rounded-full transition-all duration-300" style={{ width: '0%' }}></div>
                  </div>
                </div>

                {/* Question */}
                <div className="p-3 pointer-events-none">
                  <h2 className="text-sm font-semibold text-gray-800 mb-3 text-center">{selectedQuestion.label.replace(/\{\{speakerName\}\}/g, speakerName).replace('{speaker}', speakerName)}</h2>
                  {selectedQuestion.description && <p className="text-gray-600 mb-3 text-center text-xs">{selectedQuestion.description.replace(/\{\{speakerName\}\}/g, speakerName).replace('{speaker}', speakerName)}</p>}

                  {/* Render question input exactly like FeedbackPage */}
                  {renderQuestionInput(selectedQuestion)}

                  {/* Skip button if question can be skipped */}
                  {selectedQuestion.canSkip && (
                    <Button onClick={() => {}} disabled={true} variant="outline" className="mt-4 text-gray-500 hover:text-[#5167b2]">
                      Skip this question
                    </Button>
                  )}
                </div>

                {/* Tooltip */}
                {showTooltip && (
                  <div
                    className="fixed pointer-events-none z-50"
                    style={{
                      left: mousePosition.x + 5,
                      top: mousePosition.y + 15,
                    }}
                  >
                    <div className="bg-gray-800 text-white text-xs px-2 py-1 rounded shadow-lg whitespace-nowrap">This is a preview of what your audience would see.</div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center text-gray-500 py-8">
                <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-gray-100 flex items-center justify-center">
                  <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <p className="text-sm font-medium text-gray-700 mb-1">No question selected</p>
                <p className="text-xs text-gray-500">Select a question from &quot;All Questions&quot; to preview it here</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
